import { BULK_ACTIONS, isBulkActionAllowedFromQueue } from '@truepill/tpos-types'
import type {
  BulkActionParams,
  BulkActionSourceQueue,
  BulkActionType,
  CreateBulkActionStepArgs,
  RerouteParams,
  ResolveTriageParams,
  TriageParams,
} from '@truepill/tpos-types'
import type { BulkAction, BulkActionStepInput, BulkActionStepGql, BulkActionStepCounts } from 'types'
import { TriageReasonMap } from './triageReasons'

export const getBulkActionLabel = (type: BulkActionType): string => BULK_ACTIONS[type].label

export const getBulkActionTypeFromLabel = (label: string): BulkActionType | undefined =>
  (Object.keys(BULK_ACTIONS) as BulkActionType[]).find(type => getBulkActionLabel(type) === label)

export const getBulkActionOptions = (sourceQueue?: BulkActionSourceQueue, isOtcOnly = false): string[] => {
  const types = Object.keys(BULK_ACTIONS) as BulkActionType[]
  if (!sourceQueue) return types.map(getBulkActionLabel)

  return types.filter(type => isBulkActionAllowedFromQueue(type, sourceQueue, isOtcOnly)).map(getBulkActionLabel)
}

export const getBulkActionParamsSummary = (
  step: BulkActionStepInput,
  getLocationNameById: (id: string) => string,
): string | undefined => {
  switch (step.type) {
    case 'reroute': {
      const locationId = step.rerouteParams?.locationId
      return locationId ? getLocationNameById(locationId) || locationId : ''
    }
    case 'triage': {
      const entries = step.triageParams?.entries ?? []
      const filled = entries.filter(e => e.reason !== '')
      if (filled.length === 0) return undefined
      if (filled.length === 1) {
        const entry = filled[0]
        return entry.selectedReason || entry.reason
      }
      return `${filled.length} reasons`
    }
    case 'resolve_triage': {
      const reason = step.resolveTriageParams?.reason
      return reason ? TriageReasonMap[reason] : ''
    }
  }
}

export const buildActionStep = (type: BulkActionType, params: BulkActionParams): CreateBulkActionStepArgs => {
  switch (type) {
    case 'triage':
      return { type, triageParams: params as TriageParams }
    case 'reroute':
      return { type, rerouteParams: params as RerouteParams }
    case 'resolve_triage':
      return { type, resolveTriageParams: params as ResolveTriageParams }
  }
}

export const isBulkActionComplete = (ba: BulkAction): boolean => {
  return ba.steps.every(step => (step.results?.length ?? 0) === ba.orderIds.length)
}

export const isBulkActionAborted = (ba: BulkAction): boolean => !!ba.abortedAt

// Terminal = nothing more will happen: either every order was processed, or the job was aborted.
export const isBulkActionTerminal = (ba: BulkAction): boolean => isBulkActionAborted(ba) || isBulkActionComplete(ba)

export enum StepState {
  Pending = 'pending',
  Running = 'running',
  Completed = 'completed',
  Aborted = 'aborted',
}

export const getStepState = (ba: BulkAction, stepIndex: number): StepState => {
  const step = ba.steps[stepIndex]
  if (!step) return StepState.Pending
  if (step.results.length === ba.orderIds.length) return StepState.Completed
  // Step is incomplete. If the job was aborted, the stopped step and any later steps are
  // terminal-but-incomplete rather than still running or pending.
  if (isBulkActionAborted(ba)) return StepState.Aborted
  const prior = ba.steps[stepIndex - 1]
  if (prior && prior.results.length < ba.orderIds.length) return StepState.Pending
  return StepState.Running
}

export const formatStepLabel = (stepIndex: number, action: BulkActionType): string =>
  `Step ${stepIndex + 1} — ${getBulkActionLabel(action)}`

export const getStepCounts = (step: BulkActionStepGql, totalOrders: number): BulkActionStepCounts => {
  let success = 0
  let failed = 0
  let skipped = 0
  for (const result of step.results) {
    if (result.status === 'success') success += 1
    else if (result.status === 'failed') failed += 1
    else if (result.status === 'skipped') skipped += 1
  }
  return { success, failed, skipped, processed: step.results.length, total: totalOrders }
}
